import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SpinnerVisibilityService } from 'ng-http-loader';
import { forkJoin, of, switchMap } from 'rxjs';
import { CategoryManagementService } from '../../services/category-management.service';
import { AdminCategory, AdminSubcategory } from '../../model/category-management-dto';

@Component({
  selector: 'app-subcategory-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule
  ],
  templateUrl: './subcategory-dialog.component.html',
  styleUrl: './subcategory-dialog.component.scss'
})
export class SubcategoryDialogComponent implements OnInit {

  form: FormGroup;
  subcategory?: AdminSubcategory;
  categories: AdminCategory[] = [];

  iconFile: File | null = null;
  iconPreview: string | null = null;
  titleImageFile: File | null = null;
  titleImagePreview: string | null = null;
  errorMessage: string | null = null;

  constructor(
    private fb: FormBuilder,
    private service: CategoryManagementService,
    private spinner: SpinnerVisibilityService,
    public dialogRef: MatDialogRef<SubcategoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      category: AdminCategory;
      subcategory?: AdminSubcategory;
      categories: AdminCategory[];
    }
  ) {
    this.subcategory = data.subcategory;
    this.categories = data.categories || [];
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      subcategoryName: [this.subcategory?.subcategoryName || '', Validators.required],
      categoryId: [this.subcategory?.categoryId || this.data.category?.categoryId, Validators.required],
      active: [this.subcategory ? this.subcategory.active : true]
    });
    this.iconPreview = this.subcategory?.imageIconUrl || null;
    this.titleImagePreview = this.subcategory?.imageTitleUrl || null;
  }

  get isEdit(): boolean {
    return !!this.subcategory;
  }

  onIconSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      this.iconFile = input.files[0];
      const reader = new FileReader();
      reader.onload = () => this.iconPreview = reader.result as string;
      reader.readAsDataURL(this.iconFile);
    }
  }

  onTitleImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      this.titleImageFile = input.files[0];
      const reader = new FileReader();
      reader.onload = () => this.titleImagePreview = reader.result as string;
      reader.readAsDataURL(this.titleImageFile);
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (!this.isEdit && (!this.iconFile || !this.titleImageFile)) {
      this.errorMessage = 'El icono y la imagen de título son requeridos';
      return;
    }

    this.errorMessage = null;
    this.spinner.show();

    const uploads = {
      icon: this.iconFile ? this.service.uploadImage(this.iconFile, 'subcategory-icons') : of(null),
      title: this.titleImageFile ? this.service.uploadImage(this.titleImageFile, 'subcategory-images') : of(null)
    };

    forkJoin(uploads).pipe(
      switchMap(({ icon, title }: any) => {
        const value = this.form.value;
        const iconFileId = icon?.data?.fileId;
        const titleFileId = title?.data?.fileId;

        if (this.isEdit) {
          const body: any = {
            subcategoryName: value.subcategoryName,
            categoryId: value.categoryId,
            active: value.active
          };
          if (iconFileId) body.imageIconFileId = iconFileId;
          if (titleFileId) body.imageTitleFileId = titleFileId;
          return this.service.updateSubcategory(this.subcategory!.subcategoryId!, body);
        }

        return this.service.createSubcategory({
          categoryId: value.categoryId,
          subcategoryName: value.subcategoryName,
          active: value.active,
          imageIconFileId: iconFileId,
          imageTitleFileId: titleFileId,
          fields: []
        });
      })
    ).subscribe({
      next: () => {
        this.spinner.hide();
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.spinner.hide();
        console.error(err);
        this.errorMessage = err?.error?.message || 'Error guardando la subcategoría';
      }
    });
  }
}
